import { useCallback, useState } from 'react';
import { FiltersMap, OptionalFiltersMap } from '../../model';
import { CollectionState, PaginationModeRef } from '../../state';
import {
  useWixPatternsContainer,
  useWixPatternsContainerOverrides,
} from '../../providers';
import { CollectionConfigWithConditionals } from '../collectionConfigPartials';
import { createCollection as createCollectionBase } from '../createCollection';
import { createCollectionConfig } from '../createCollectionConfig';

export type CollectionFactory = <T, F extends FiltersMap = OptionalFiltersMap>(
  config: CollectionConfigWithConditionals<T, F>,
) => CollectionState<T, F>;

export function usePrivateCreateCollection() {
  const container = useWixPatternsContainer();

  const overrides = useWixPatternsContainerOverrides();

  return useCallback(
    <T, F extends FiltersMap = OptionalFiltersMap>(
      config: CollectionConfigWithConditionals<T, F>,
      options: {
        paginationModeRef?: PaginationModeRef;
      } = {},
    ) =>
      createCollectionBase(container, createCollectionConfig(config), {
        ...options,
        overrides,
      }),
    [],
  );
}

export function useCreateCollection(): CollectionFactory {
  const createCollection = usePrivateCreateCollection();

  return useCallback<CollectionFactory>(
    <T, F extends FiltersMap = OptionalFiltersMap>(
      config: CollectionConfigWithConditionals<T, F>,
    ) => createCollection(config),
    [],
  );
}

export function useCollection<T, F extends FiltersMap = OptionalFiltersMap>(
  config: CollectionConfigWithConditionals<T, F>,
): CollectionState<T, F> {
  const createCollection = useCreateCollection();

  const [collection] = useState(() => createCollection<T, F>(config));

  return collection;
}
